import { z } from 'zod';
import {
  CargoCarrierPaymentStatusSchema,
  CargoClientPaymentStatusSchema,
  CargoStatusSchema,
} from './enums.js';

// Cargo registry article ("Артикул") wire skeleton (#1227). FOUNDATION ONLY:
// a basic entity projection consistent with the Prisma `CargoArticle` model.
// The full create/update/query API envelopes (validation rules, list/visibility
// filters, derived issued-state, days-without-payment) belong to #1228/#1230
// and EXTEND this skeleton — do not inline them here.

/**
 * @aiPurpose canonical nonnegative 2-decimal money string for
 *   `chinaPaymentAmount`. Crosses the wire as a string (never a JS float) so
 *   the backend `Decimal(12,2)` precision survives JSON round-tripping. The
 *   nonneg constraint is enforced here rather than at the DB layer.
 * @aiInvariant shape `\d+\.\d{2}` (no leading `-`).
 */
export const CARGO_NONNEG_MONEY_2DP_REGEX = /^\d+\.\d{2}$/;
export const CargoNonnegMoneySchema = z
  .string()
  .regex(
    CARGO_NONNEG_MONEY_2DP_REGEX,
    'must be a nonnegative decimal string with 2 fraction digits',
  );

/**
 * @aiPurpose signed 2-decimal money string for fields that may legitimately be
 *   negative (e.g. a payment-document running `balance` serialized via
 *   `Decimal.toFixed(2)`). The {@link CargoNonnegMoneySchema} sibling is for
 *   amounts that cannot be negative.
 * @aiInvariant shape `-?\d+\.\d{2}` (optional leading `-`).
 */
export const CARGO_SIGNED_MONEY_2DP_REGEX = /^-?\d+\.\d{2}$/;
export const CargoSignedMoney2dpSchema = z
  .string()
  .regex(
    CARGO_SIGNED_MONEY_2DP_REGEX,
    'must be a decimal string with 2 fraction digits (sign optional)',
  );

/**
 * @aiPurpose positive exchange rate string for a foreign-currency cargo
 *   payment (#1933). Crosses the wire as a string (never a JS float) so the
 *   backend rate precision survives JSON round-tripping. 1–6 fraction digits, or
 *   a bare integer.
 * @aiInvariant shape `\d+(\.\d{1,6})?` AND numeric value strictly `> 0` (the
 *   regex alone admits `'0'`, which the refine rejects).
 */
export const CARGO_EXCHANGE_RATE_REGEX = /^\d+(\.\d{1,6})?$/;
export const CargoExchangeRateSchema = z
  .string()
  .regex(
    CARGO_EXCHANGE_RATE_REGEX,
    'must be a decimal string with up to 6 fraction digits (or an integer)',
  )
  .refine((v) => Number(v) > 0, 'exchange rate must be positive');

// WHY: the source persists these quantity/money fields as free-text
// CharField(50) values that may contain spaces, commas, or a trailing `+`.
// The faithful port keeps them as dirty nullable strings here; the migration
// (#1233) cleans them and later issues may tighten the type. A registry row
// must round-trip a value the importer wrote verbatim, so the wire layer is
// permissive (nullable string) by design.
const DirtyFreeText = z.string().nullable();

/**
 * @aiPurpose Cargo registry article entity skeleton (#1227). Mirrors the
 *   Prisma `CargoArticle` columns. Dates are ISO-8601 strings; `carrier` and
 *   `pathFormat` are stored as free strings. The closed-enum input validation
 *   (against `CargoCarrierSchema` / `CargoPathFormatSchema`) was relaxed to free
 *   text in #1945, so the mutation boundary no longer pins these to those
 *   choice lists; they are never pinned here either.
 * @aiInvariant FOUNDATION skeleton — `.strict()` is intentionally NOT applied
 *   so #1228 can extend the projection without a coordinated wire break.
 */
export const CargoArticleSchema = z.object({
  id: z.string().uuid(),
  // Migration traceability (#1233): NextKargo source id + import batch id.
  legacySourceId: z.string().nullable(),
  migrationBatchId: z.string().nullable(),

  article: z.string(),
  normalizedArticle: z.string(),
  responsibleManagerId: z.string().uuid().nullable(),

  carrier: z.string().nullable(),
  pathFormat: z.string().nullable(),
  status: CargoStatusSchema,

  nameGoods: z.string().nullable(),

  numberOfSeats: DirtyFreeText,
  weight: DirtyFreeText,
  volume: DirtyFreeText,
  transportationTariff: DirtyFreeText,
  transportationTariffForClients: DirtyFreeText,
  costGoods: DirtyFreeText,
  insuranceCost: DirtyFreeText,
  packagingCost: DirtyFreeText,
  totalCost: DirtyFreeText,
  addressTransportationCost: DirtyFreeText,
  prr: DirtyFreeText,
  tatCost: DirtyFreeText,

  chinaPaymentAmount: CargoNonnegMoneySchema.nullable(),
  // WHY: NextKargo "склад, Китай" fields shipped 2026-06-08 (#1605). Optional
  // (not just nullable) so the wire item schema tolerates their absence until
  // #1606 wires to-wire.ts to emit them; required-nullable would break
  // CargoArticleWireSchema.parse on any GET /cargo response before #1606.
  chinaWarehouseDispatchAmount: CargoNonnegMoneySchema.nullable().optional(),
  chinaWarehouseInspectionAmount: CargoNonnegMoneySchema.nullable().optional(),
  chinaWarehousePackagingAmount: CargoNonnegMoneySchema.nullable().optional(),

  paidByClientStatus: CargoClientPaymentStatusSchema,
  // WHY (#1933): desired client-payment status the Manager submits; null = not
  // chosen. Optional for rollout (old payloads omit it until #1936 emits it).
  requestedClientPaymentStatus: z.enum(['paid', 'partially_paid']).nullable().optional(),
  timePaidByClientStatus: z.string().datetime({ offset: true }).nullable(),
  paymentToCarrierStatus: CargoCarrierPaymentStatusSchema,

  timeCargoArrivalToRf: z.string().datetime({ offset: true }).nullable(),
  timeCargoRelease: z.string().datetime({ offset: true }).nullable(),
  timeFromChina: z.string().datetime({ offset: true }).nullable(),

  sourceFileId: z.string().uuid().nullable(),

  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),

  // Soft-delete (#1227, net-new vs source): a deleted row keeps its history.
  deletedAt: z.string().datetime({ offset: true }).nullable(),
  deletedById: z.string().uuid().nullable(),
});
export type CargoArticle = z.infer<typeof CargoArticleSchema>;
